import React, { useState, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  CircularProgress,
  Alert,
  styled,
  LinearProgress,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  Checkbox,
  FormControlLabel,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Collapse
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import { importExportApi } from '../services/api';
import dayjs from 'dayjs';

// Visually hidden input for file upload
const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

// Styled upload zone
const UploadZone = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  border: `2px dashed ${theme.palette.primary.main}`,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.default,
  cursor: 'pointer',
  transition: 'all 0.3s',
  minHeight: 200,
  '&:hover': {
    borderColor: theme.palette.secondary.main,
    backgroundColor: theme.palette.action.hover,
  }
}));

// Error Table Cell with highlighting
const ErrorTableCell = styled(TableCell)(({ theme, hasError }) => ({
  backgroundColor: hasError ? theme.palette.error.light : 'transparent',
  color: hasError ? theme.palette.error.contrastText : 'inherit',
  border: hasError ? `2px solid ${theme.palette.error.main}` : 'none',
  position: 'relative',
  '&::after': hasError ? {
    content: '"!"',
    position: 'absolute',
    top: 2,
    right: 2,
    color: theme.palette.error.main,
    fontWeight: 'bold',
    fontSize: '12px'
  } : {}
}));

const ImportDialog = ({ open, onClose, onSuccess }) => {
  const theme = useTheme();
  
  // Import state
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isJobRunning, setIsJobRunning] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [previewData, setPreviewData] = useState(null);
  
  // Error handling state
  const [validationErrors, setValidationErrors] = useState(null);
  const [showErrorTable, setShowErrorTable] = useState(false);
  const [errorTableData, setErrorTableData] = useState(null);
  
  // Digital vendor handling
  const [digitalVendors, setDigitalVendors] = useState([]);
  const [selectedVendors, setSelectedVendors] = useState({});
  const [showVendorDialog, setShowVendorDialog] = useState(false);
  
  // Steps for the import process (in English)
  const steps = ['Select File', 'Validate Data', 'Import Data'];

  // Import type and snapshot handling
  const [monthSnapshot, setMonthSnapshot] = useState(dayjs().format('YYYY-MM'));
  const [importType, setImportType] = useState('regular'); // 'regular', 'snapshot', 'snapshot-direct'
  
  // Git-like import functionality
  const [commitMessage, setCommitMessage] = useState('');
  const [diffAnalysis, setDiffAnalysis] = useState(null);
  const [requiresCommitMessage, setRequiresCommitMessage] = useState(true);

  // Optimized handlers to prevent re-renders
  const handleCommitMessageChange = useCallback((event) => {
    setCommitMessage(event.target.value);
  }, []);

  const handleImportTypeChange = useCallback((event) => {
    setImportType(event.target.value);
  }, []);

  const handleMonthSnapshotChange = useCallback((event) => {
    setMonthSnapshot(event.target.value);
  }, []);

  // Handle template download
  const handleDownloadTemplate = async () => {
    try {
      setLoading(true);
      const response = await importExportApi.downloadImportTemplate();
      
      // Create blob and download
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'AMS_import_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setSuccessMessage('Template downloaded successfully!');
    } catch (error) {
      console.error('Error downloading template:', error);
      setErrorMessage('Failed to download template. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle file selection
  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setActiveStep(1);
    setErrorMessage('');
    setSuccessMessage('');
    setValidationResult(null);
    setValidationErrors(null);
    setShowErrorTable(false);
    
    setSuccessMessage(`File "${selectedFile.name}" selected successfully`);
    
    // Auto-validate after file selection
    validateImport(selectedFile);
  };

  // Validate import data
  const validateImport = async (selectedFile) => {
    if (!selectedFile && !file) {
      setErrorMessage('Please select a file first');
      return;
    }

    const fileToUse = selectedFile || file;
    setValidating(true);
    setErrorMessage('');
    setValidationErrors(null);

    try {
      const formData = new FormData();
      formData.append('file', fileToUse);
      
      // Use appropriate validation for each import type
      if (importType === 'snapshot') {
        formData.append('type', 'snapshot');
      } else {
        formData.append('type', 'combined');
      }

      const response = await importExportApi.validateCampaignImport(formData);

      // If validation is successful
      setValidationResult(response.data);
      setActiveStep(2);
      
      // Handle diff analysis if available (only for regular imports)
      if (response.data.diff_analysis && importType === 'regular') {
        setDiffAnalysis(response.data.diff_analysis);
        setRequiresCommitMessage(response.data.requires_commit_message || false);
        
        const { changes_summary } = response.data.diff_analysis;
        if (changes_summary.existing_campaigns_count > 0) {
          setSuccessMessage(
            `Validation successful! Found ${changes_summary.new_campaigns_count} new campaigns and ${changes_summary.existing_campaigns_count} existing campaigns with ${changes_summary.total_changes} changes. Please enter a commit message.`
          );
        } else {
          setSuccessMessage('Validation successful! All campaigns are new and ready to import.');
        }
      } else {
        // For snapshot imports, simpler message
        if (importType === 'snapshot') {
          setSuccessMessage('Validation successful! Snapshot data is ready to import.');
          setRequiresCommitMessage(false); // No commit message needed for snapshots
        } else {
          setSuccessMessage('Validation successful! Data is ready to import.');
        }
      }

      // Check if we have digital media types and vendor data
      if (response.data.digital_media_types && response.data.digital_media_types.length > 0) {
        try {
          const vendorsResponse = await importExportApi.getDigitalVendors();
          setDigitalVendors(vendorsResponse.data || []);

          if (response.data.digital_media_types.length > 0) {
            const initialVendors = {};
            response.data.digital_media_types.forEach(mediaType => {
              initialVendors[mediaType] = vendorsResponse.data && vendorsResponse.data.length > 0 
                ? vendorsResponse.data[0].name 
                : 'Unknown';
            });
            setSelectedVendors(initialVendors);
            setShowVendorDialog(true);
          }
        } catch (error) {
          console.error('Error fetching vendors:', error);
        }
      }

      // Set preview data
      if (response.data.preview) {
        setPreviewData(response.data.preview);
      }
    } catch (error) {
      console.error('Validation error:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      
      const errorData = error.response?.data || {};
      console.log('Processing errorData:', errorData);
      
      // Handle detailed validation errors with row-level information
      if (errorData.validation_errors && !errorData.validation_errors.is_valid) {
        setValidationErrors(errorData.validation_errors);
        setErrorMessage(`Validation failed: ${errorData.validation_errors.summary.error_rows} rows have errors. Click "View Error Details" to see specific issues.`);
        
        // Prepare error table data for display
        prepareErrorTableData(errorData.validation_errors);
      } else {
        // Handle server error responses - preserve original format for date errors
        let errorMessage = 'Error validating file. Please ensure the file format is correct.';
        
        // Try multiple ways to extract the error message
        if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData.errors) {
          if (Array.isArray(errorData.errors)) {
            // For arrays, check if it's date format errors - preserve newlines
            const errorsText = errorData.errors.join('\n');
            if (errorsText.includes('Date format errors:') || errorsText.includes('Invalid') && errorsText.includes('format')) {
              errorMessage = errorsText; // Preserve formatting for date errors
            } else {
              errorMessage = errorData.errors.join('; '); // Use semicolon for other errors
            }
          } else {
            errorMessage = String(errorData.errors);
          }
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        // ADDITIONAL: Check if error is in the format that api.js wraps it
        // Sometimes the real error is nested deeper
        if (error.response && error.response.status === 400) {
          // This is likely a validation error from backend
          const rawResponseData = error.response.data;
          if (typeof rawResponseData === 'string' && rawResponseData.includes('Date format errors:')) {
            errorMessage = rawResponseData;
          } else if (rawResponseData && rawResponseData.errors && typeof rawResponseData.errors === 'string') {
            errorMessage = rawResponseData.errors;
          }
        }
        
        console.log('Final error message for general case:', errorMessage);
        setErrorMessage(errorMessage);
      }
    } finally {
      setValidating(false);
    }
  };

  // Prepare error table data from validation errors
  const prepareErrorTableData = async (validationErrors) => {
    if (!file) return;

    try {
      // Re-read the file to get raw data for display
      const formData = new FormData();
      formData.append('file', file);
      
      // We'll create a simple representation of the errors
      const errorRows = validationErrors.row_errors;
      const errorTableRows = [];
      
      // Get all column names from first error row (if available)
      const allColumns = new Set();
      errorRows.forEach(rowError => {
        Object.keys(rowError.errors).forEach(field => allColumns.add(field));
      });
      
      // Add common columns that might not have errors
      ['Brand', 'Campaign_Name', 'Media_Type_Channel', 'Month', 'Given_Cost_Excl_VAT_VND'].forEach(col => allColumns.add(col));
      
      setErrorTableData({
        columns: Array.from(allColumns),
        errorRows: errorRows,
        totalRows: validationErrors.summary.total_rows
      });
    } catch (error) {
      console.error('Error preparing error table data:', error);
    }
  };

  // Handle import execution
  const handleImport = async () => {
    if (!file) {
      setErrorMessage('No file selected');
      return;
    }

    // Set a default commit message if none provided
    const finalCommitMessage = commitMessage.trim() || `Import on ${new Date().toLocaleString()}`;

    setLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const formData = new FormData();
      formData.append('file', file);
      
      let response;
      
      // Handle different import types
      if (importType === 'snapshot') {
        formData.append('month', monthSnapshot);
        formData.append('note', finalCommitMessage);
        
        // Use snapshot endpoint
        response = await importExportApi.importSnapshot(formData);
      } else {
        formData.append('type', 'combined');
        formData.append('month_snapshot', monthSnapshot);
        formData.append('import_type', importType);
        
        // Always send commit message - backend requires it for git-like imports
        formData.append('commit_message', finalCommitMessage);

        // Add vendor selections for digital media types
        if (Object.keys(selectedVendors).length > 0) {
          formData.append('vendor_mapping', JSON.stringify(selectedVendors));
        }

        response = await importExportApi.importCampaigns(formData);
      }

      if (response.data.status === 'success') {
        setSuccessMessage(response.data.message || 'Import completed successfully');
        
        // Show detailed summary if available
        if (importType === 'snapshot' && response.data.stats) {
          const stats = response.data.stats;
          setSuccessMessage(
            `Snapshot import completed successfully! ` +
            `Campaigns: ${stats.campaigns_created} created, ${stats.campaigns_updated} updated. ` +
            `Media Types: ${stats.media_types_created} created, ${stats.media_types_updated} updated. ` +
            `Monthly Data: ${stats.monthly_data_created} created, ${stats.monthly_data_updated} updated.`
          );
        } else if (response.data.summary) {
          const summary = response.data.summary;
          setSuccessMessage(
            `Import completed successfully! ` +
            `New campaigns: ${summary.new_campaigns_imported || 0}, ` +
            `Updated campaigns: ${summary.existing_campaigns_updated || 0}, ` +
            `Backup versions: ${summary.backup_versions_created || 0}`
          );
        }
        
        // Check for errors even in successful imports and show as warnings
        if (response.data.summary && response.data.summary.errors && response.data.summary.errors.length > 0) {
          const errorCount = response.data.summary.errors.length;
          const errorList = response.data.summary.errors.map(err => `${err.campaign_name}: ${err.error}`).join('\n');
          setErrorMessage(
            `Import completed with ${errorCount} warnings:\n\n${errorList}\n\nThese campaigns may need attention.`
          );
        }
        
        if (onSuccess) {
          onSuccess(response.data);
        }
        
        // Don't auto-close - let user manually close to review results
        // setTimeout(() => {
        //   handleReset();
        //   onClose();
        // }, 3000);
      } else {
        setErrorMessage(response.data.message || 'Import failed');
        if (response.data.errors && response.data.errors.length > 0) {
          setErrorMessage(response.data.errors.join('; '));
        }
      }
    } catch (error) {
      console.error('Import error:', error);
      const errorData = error.response?.data || {};
      
      let errorMessage = 'Import failed. Please try again.';
      if (errorData.detail) {
        errorMessage = errorData.detail;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else if (errorData.errors && Array.isArray(errorData.errors)) {
        errorMessage = errorData.errors.join('; ');
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setErrorMessage(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleVendorChange = (mediaType, vendor) => {
    setSelectedVendors(prev => ({
      ...prev,
      [mediaType]: vendor
    }));
  };

  const handleReset = () => {
    setFile(null);
    setValidationResult(null);
    setValidationErrors(null);
    setErrorTableData(null);
    setShowErrorTable(false);
    setSuccessMessage('');
    setErrorMessage('');
    setActiveStep(0);
    setLoading(false);
    setValidating(false);
    setPreviewData(null);
    setCommitMessage('');
    setDiffAnalysis(null);
    setRequiresCommitMessage(true);
    setDigitalVendors([]);
    setSelectedVendors({});
    setShowVendorDialog(false);
  };

  const renderErrorTable = useMemo(() => {
    if (!errorTableData || !showErrorTable) return null;

    return (
      <Accordion expanded={showErrorTable} onChange={() => setShowErrorTable(!showErrorTable)}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" color="error">
            <ErrorIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Error Details ({validationErrors.summary.error_rows} rows with errors)
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Red highlighted cells indicate specific field errors. Hover over error cells to see detailed error messages.
          </Typography>
          
          <TableContainer component={Paper} sx={{ maxHeight: 600, mb: 2 }}>
            <Table stickyHeader size="small" sx={{ '& .MuiTableCell-root': { padding: '4px 8px', fontSize: '0.75rem' } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 50, maxWidth: 70 }}><strong>Row #</strong></TableCell>
                  {errorTableData.columns.map(column => (
                    <TableCell key={column} sx={{ minWidth: 80, maxWidth: 150 }}>
                      <strong style={{ fontSize: '0.7rem' }}>{column.replace(/_/g, ' ')}</strong>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {errorTableData.errorRows.map((rowError, index) => (
                  <TableRow key={index} sx={{ height: 32 }}>
                    <TableCell>
                      <Chip 
                        label={rowError.row_index} 
                        color="error" 
                        size="small"
                        sx={{ height: 20, fontSize: '0.65rem', minWidth: 40 }}
                      />
                    </TableCell>
                    {errorTableData.columns.map(column => {
                      const hasError = rowError.errors[column];
                      return (
                        <ErrorTableCell 
                          key={column} 
                          hasError={!!hasError}
                          sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}
                        >
                          {hasError ? (
                            <Tooltip title={hasError} arrow>
                              <Box sx={{ cursor: 'help' }}>
                                <Typography variant="caption" color="error" sx={{ fontSize: '0.65rem' }}>
                                  ERROR: {hasError}
                                </Typography>
                              </Box>
                            </Tooltip>
                          ) : (
                            <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.65rem' }}>
                              (No error)
                            </Typography>
                          )}
                        </ErrorTableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          <Alert severity="info">
            <Typography variant="body2">
              <strong>Total:</strong> {validationErrors.summary.total_rows} rows analyzed, {validationErrors.summary.error_rows} rows with errors, {validationErrors.summary.error_fields} total field errors
            </Typography>
          </Alert>
        </AccordionDetails>
      </Accordion>
    );
  }, [errorTableData, showErrorTable, validationErrors]);

  const renderVendorDialog = () => {
    if (!showVendorDialog) return null;

    return (
      <Dialog open={showVendorDialog} onClose={() => setShowVendorDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Select Digital Vendors</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Your import contains digital media types. Please select the appropriate vendor for each:
          </Typography>
          
          {Object.keys(selectedVendors).map(mediaType => (
            <Box key={mediaType} sx={{ mb: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Vendor for {mediaType}</InputLabel>
                <Select
                  value={selectedVendors[mediaType] || ''}
                  onChange={(e) => handleVendorChange(mediaType, e.target.value)}
                  label={`Vendor for ${mediaType}`}
                >
                  {digitalVendors.map(vendor => (
                    <MenuItem key={vendor.id} value={vendor.name}>
                      {vendor.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowVendorDialog(false)}>
            Continue with Import
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderProgressIndicator = () => {
    if (validating) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 2 }}>
          <CircularProgress size={20} />
          <Typography>Validating file...</Typography>
        </Box>
      );
    }

    if (loading) {
      return (
        <Box sx={{ my: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <CircularProgress size={20} />
            <Typography>Importing data...</Typography>
          </Box>
          {uploadProgress > 0 && (
            <LinearProgress variant="determinate" value={uploadProgress} />
          )}
        </Box>
      );
    }

    return null;
  };

  const renderPreview = () => {
    if (!previewData || previewData.length === 0) return null;

    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="h6" gutterBottom>
          Data Preview ({previewData.length} rows)
        </Typography>
        <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
          <Table size="small" sx={{ '& .MuiTableCell-root': { padding: '6px 8px', fontSize: '0.75rem' } }}>
            <TableHead>
              <TableRow>
                {Object.keys(previewData[0] || {}).map(key => (
                  <TableCell key={key} sx={{ minWidth: 80, maxWidth: 200 }}>
                    <strong style={{ fontSize: '0.7rem' }}>{key.replace(/_/g, ' ')}</strong>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {previewData.map((row, index) => (
                <TableRow key={index} sx={{ height: 36 }}>
                  {Object.values(row).map((value, cellIndex) => (
                    <TableCell key={cellIndex} sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                        {value !== null && value !== undefined ? String(value) : ''}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const renderDiffSummary = useMemo(() => {
    if (!diffAnalysis) return null;

    const { changes_summary, new_campaigns, existing_campaigns } = diffAnalysis;

    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="h6" gutterBottom>
          Import Summary
        </Typography>
        
        <Grid container spacing={1} sx={{ mb: 2 }}>
          <Grid item xs={4}>
            <Paper sx={{ p: 1.5, textAlign: 'center', backgroundColor: 'success.light' }}>
              <Typography variant="h5" color="success.dark" sx={{ fontWeight: 'bold' }}>
                {changes_summary.new_campaigns_count}
              </Typography>
              <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>New Campaigns</Typography>
            </Paper>
          </Grid>
          <Grid item xs={4}>
            <Paper sx={{ p: 1.5, textAlign: 'center', backgroundColor: 'warning.light' }}>
              <Typography variant="h5" color="warning.dark" sx={{ fontWeight: 'bold' }}>
                {changes_summary.existing_campaigns_count}
              </Typography>
              <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Existing Campaigns</Typography>
            </Paper>
          </Grid>
          <Grid item xs={4}>
            <Paper sx={{ p: 1.5, textAlign: 'center', backgroundColor: 'info.light' }}>
              <Typography variant="h5" color="info.dark" sx={{ fontWeight: 'bold' }}>
                {changes_summary.total_changes}
              </Typography>
              <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Total Changes</Typography>
            </Paper>
          </Grid>
        </Grid>

        {changes_summary.requires_backup && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>Data Safety:</strong> Backup versions will be automatically created before applying changes.
          </Alert>
        )}


      </Box>
    );
  }, [diffAnalysis]);

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="xl" 
        fullWidth
        PaperProps={{
          style: {
            height: '90vh',
            maxHeight: '90vh',
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5">Import Campaign Data</Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ maxHeight: 'calc(90vh - 140px)', overflowY: 'auto', pb: 1 }}>
          {/* Stepper */}
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Import Type Selection */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 2 }}>
                <strong>Import Types:</strong>
                <br />• <strong>Regular:</strong> Git-like import with change detection, commit message, and direct database updates
                <br />• <strong>Snapshot:</strong> Creates SystemMonthlySnapshot JSON record for specified month (no database updates)
              </Alert>
            </Grid>
            <Grid item xs={importType === 'snapshot' ? 6 : 12}>
              <FormControl fullWidth>
                <InputLabel>Import Type</InputLabel>
                <Select
                  value={importType}
                  onChange={handleImportTypeChange}
                  label="Import Type"
                >
                  <MenuItem value="regular">Regular Import (Git-like with database updates)</MenuItem>
                  <MenuItem value="snapshot">Snapshot Import (JSON record only)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {importType === 'snapshot' && (
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="month"
                  label="Month Snapshot"
                  value={monthSnapshot}
                  onChange={handleMonthSnapshotChange}
                  InputLabelProps={{ shrink: true }}
                  helperText="Required for snapshot imports"
                />
              </Grid>
            )}
          </Grid>

          {/* Download Template Section */}
          {activeStep === 0 && (
            <Box sx={{ mb: 3 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Need a template?</strong> Download the official AMS import template with the correct format and sample data.
                </Typography>
              </Alert>
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadTemplate}
                  disabled={loading}
                  sx={{ mb: 2 }}
                >
                  Download Import Template
                </Button>
              </Box>
            </Box>
          )}

          {/* File Upload */}
          {activeStep === 0 && (
            <UploadZone component="label">
              <CloudUploadIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Select Excel File to Import
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Supported formats: .xlsx, .xls
              </Typography>
              <Button
                component="span"
                variant="contained"
                startIcon={<CloudUploadIcon />}
              >
                Choose File
              </Button>
              <VisuallyHiddenInput
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
              />
            </UploadZone>
          )}

          {/* Progress Indicator */}
          {renderProgressIndicator()}

          {/* Success Message */}
          {successMessage && (
            <Alert severity="success" sx={{ mt: 2 }}>
              <CheckCircleIcon sx={{ mr: 1 }} />
              {successMessage}
            </Alert>
          )}

          {/* Error Message */}
          {errorMessage && (
            <Alert 
              severity="error" 
              sx={{ mt: 2 }}
              action={
                validationErrors && (
                  <Button 
                    color="inherit" 
                    size="small"
                    startIcon={<VisibilityIcon />}
                    onClick={() => setShowErrorTable(!showErrorTable)}
                  >
                    {showErrorTable ? 'Hide' : 'View'} Error Details
                  </Button>
                )
              }
            >
              <ErrorIcon sx={{ mr: 1 }} />
              {errorMessage}
            </Alert>
          )}

          {/* Error Table */}
          {renderErrorTable}

          {/* Diff Summary */}
          {renderDiffSummary}

          {/* Preview */}
          {renderPreview()}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleReset} variant="outlined" startIcon={<CloseIcon />}>
            Reset
          </Button>
          
          {activeStep === 1 && !validating && !validationResult && (
            <Button 
              onClick={() => validateImport()} 
              variant="contained"
              disabled={!file}
            >
              Validate File
            </Button>
          )}
          
          {activeStep === 2 && validationResult && !validationErrors && (
            <Button 
              onClick={handleImport} 
              variant="contained" 
              disabled={loading}
              startIcon={<CheckCircleIcon />}
            >
              {loading ? 'Importing...' : 'Import Data'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Vendor Selection Dialog */}
      {renderVendorDialog()}
    </>
  );
};

export default ImportDialog;
